import { getStoreSettingsForKiosk, getStoreLocationForKiosk } from '../EcommerceService.js';

export function extractEcomApiRecord(response) {
  if (response?.status !== 'ok') {
    return null;
  }
  const { result } = response;
  if (!result || typeof result !== 'object') {
    return null;
  }
  if (Array.isArray(result)) {
    return result[0] ?? null;
  }
  return result;
}

export function mapLocationToLegacyShippingAddress(location) {
  if (!location) {
    return {};
  }

  return {
    shopifyLocationName: location.location_name ?? '',
    shopifyLocationExternalId: location.location_external_id ?? '',
    shopifyFirstName: location.first_name ?? '',
    shopifyLastName: location.last_name ?? '',
    shopifyAddress1: location.address1 ?? '',
    shopifyAddress2: location.address2 ?? '',
    shopifyCountry: location.country ?? '',
    shopifyState: location.state ?? '',
    shopifyCity: location.city ?? '',
    shopifyZip: location.zip ?? '',
    shopifyCompany: location.company ?? '',
    shopifyPhoneNumber: location.phone ?? '',
  };
}

export function isTruthyEcomFlag(value) {
  return value === true || value === 1 || value === '1' || value === 'true';
}

export function toEcomStorageOneOrZero(value) {
  return isTruthyEcomFlag(value) ? 1 : 0;
}

export function parseLegacyShippingAddress(legacyShippingAddress) {
  if (legacyShippingAddress == null || legacyShippingAddress === 'null') {
    return {};
  }

  if (typeof legacyShippingAddress === 'string') {
    try {
      const parsed = JSON.parse(legacyShippingAddress);
      if (parsed && typeof parsed === 'object') {
        return parsed.shopifyAddress1 != null
          ? parsed
          : mapLocationToLegacyShippingAddress(parsed);
      }
    } catch {
      return {};
    }
    return {};
  }

  if (typeof legacyShippingAddress === 'object') {
    return legacyShippingAddress.shopifyAddress1 != null
      ? legacyShippingAddress
      : mapLocationToLegacyShippingAddress(legacyShippingAddress);
  }

  return {};
}

function readExistingEventOpts(storage) {
  try {
    const raw = storage.getItem('eventOpts');
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function hasOwn(object, key) {
  return Object.hasOwn(object, key);
}

export async function loadEcommerceCheckoutSettings({ eventName, eventPublicApiKey }) {
  const settings = {};

  if (!eventName || !eventPublicApiKey) {
    return settings;
  }

  try {
    const settingsResponse = await getStoreSettingsForKiosk({ eventName, eventPublicApiKey });
    if (settingsResponse?.status === 'ok') {
      const storeSettings = extractEcomApiRecord(settingsResponse);
      settings.shopDomain = storeSettings?.shop_domain?.trim() || '';
      settings.shopToken = storeSettings?.shop_token?.trim() || '';
      settings.isUpsellProductsCheck = toEcomStorageOneOrZero(storeSettings?.is_upsell_products_check);
      settings.hideProductPrice = toEcomStorageOneOrZero(storeSettings?.hide_product_price);
      settings.enabledShopComboRequired = isTruthyEcomFlag(storeSettings?.enabled_shop_combo_required);
    }
  } catch (error) {
    console.warn('Failed to load ecommerce settings:', error);
  }

  try {
    const locationResponse = await getStoreLocationForKiosk({ eventName, eventPublicApiKey });
    if (locationResponse?.status === 'ok') {
      const storeLocation = extractEcomApiRecord(locationResponse);
      settings.shippingAddress = mapLocationToLegacyShippingAddress(storeLocation);
    }
  } catch (error) {
    console.warn('Failed to load ecommerce location:', error);
  }

  return settings;
}

export function applyEcommerceCheckoutSettingsToStorage(settings, storage = localStorage, baseOpts = null) {
  const existingOpts =
    baseOpts && typeof baseOpts === 'object' ? baseOpts : readExistingEventOpts(storage);

  // Preserve non-ecommerce event opts; only overlay ecommerce_settings / location fields when provided.
  const mergedOpts = { ...existingOpts };

  if (hasOwn(settings, 'shopDomain')) {
    mergedOpts.shopDomain = settings.shopDomain;
    mergedOpts.shopToken = settings.shopToken;
    mergedOpts.isUpsellProductsCheck = settings.isUpsellProductsCheck;
    mergedOpts.hideProductPrice = settings.hideProductPrice;
    mergedOpts.enabledShopComboRequired = settings.enabledShopComboRequired;

    storage.setItem('isUpsellProductsCheck', settings.isUpsellProductsCheck);
    storage.setItem('enabledShopComboRequired', settings.enabledShopComboRequired);

    if (settings.hideProductPrice) {
      storage.setItem('hideProductPrice', settings.hideProductPrice);
    } else {
      storage.removeItem('hideProductPrice');
    }

    if (settings.shopDomain) {
      storage.setItem('shopDomain', settings.shopDomain);
    } else {
      storage.removeItem('shopDomain');
    }

    if (settings.shopToken) {
      storage.setItem('shopToken', settings.shopToken);
    } else {
      storage.removeItem('shopToken');
    }
  }

  if (hasOwn(settings, 'shippingAddress')) {
    storage.setItem('shippingAddress', JSON.stringify(settings.shippingAddress ?? {}));
  }

  storage.setItem('eventOpts', JSON.stringify(mergedOpts));
  return mergedOpts;
}
